import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { addSampleQuizzes, assignQuizzesToStudent } from '../utils/addSampleQuizzes';

export default function AdminPanel() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [studentUID, setStudentUID] = useState('');

  const handleAddQuizzes = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      const success = await addSampleQuizzes();
      if (success) {
        setMessage('✅ Sample quizzes added successfully!');
      } else {
        setMessage('❌ Failed to add sample quizzes');
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignQuizzes = async () => {
    if (!studentUID.trim()) {
      setMessage('❌ Please enter a student UID');
      return;
    }

    setLoading(true);
    setMessage('');
    
    try {
      const success = await assignQuizzesToStudent(studentUID.trim());
      if (success) {
        setMessage(`✅ Quizzes assigned to student ${studentUID}!`);
      } else {
        setMessage('❌ Failed to assign quizzes');
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">🛠️ Admin Panel</h1>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800">
              <strong>Current User:</strong> {user?.email || 'Not logged in'}
            </p>
            <p className="text-blue-800 text-sm mt-1">
              Use this panel to set up sample data for testing the ExamLock platform.
            </p>
          </div>

          {message && (
            <div className={`p-4 rounded-lg mb-6 ${
              message.includes('✅') 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          <div className="space-y-6">
            {/* Add Sample Quizzes */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">📝 Add Sample Quizzes</h2>
              <p className="text-gray-600 mb-4">
                This will add 3 sample quizzes to Firestore:
              </p>
              <ul className="text-sm text-gray-600 mb-4 space-y-1">
                <li>• Mathematics Quiz - Algebra Basics (30 min, 5 questions)</li>
                <li>• Science Quiz - Basic Chemistry (45 min, 4 questions)</li>
                <li>• History Quiz - World War II (25 min, 4 questions)</li>
              </ul>
              <button
                onClick={handleAddQuizzes}
                disabled={loading}
                className={`px-6 py-3 rounded-lg font-semibold transition duration-200 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? 'Adding...' : 'Add Sample Quizzes'}
              </button>
            </div>

            {/* Assign Quizzes to Student */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">🎓 Assign Quizzes to Student</h2>
              <p className="text-gray-600 mb-4">
                Enter a student's UID to assign all sample quizzes to them:
              </p>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student UID
                  </label>
                  <input
                    type="text"
                    value={studentUID}
                    onChange={(e) => setStudentUID(e.target.value)}
                    placeholder="e.g., abc123def456ghi789"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    You can find the student UID in Firebase Console or after they register
                  </p>
                </div>
                <button
                  onClick={handleAssignQuizzes}
                  disabled={loading || !studentUID.trim()}
                  className={`px-6 py-3 rounded-lg font-semibold transition duration-200 ${
                    loading || !studentUID.trim()
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {loading ? 'Assigning...' : 'Assign Quizzes'}
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">📋 Testing Instructions</h3>
              <ol className="text-sm text-yellow-700 space-y-2">
                <li>1. First, add the sample quizzes using the button above</li>
                <li>2. Create a student account at <code>/student-register</code></li>
                <li>3. Check the Firebase Console to get the student's UID</li>
                <li>4. Use the "Assign Quizzes" section to assign quizzes to that student</li>
                <li>5. Log in as the student to see assigned quizzes</li>
                <li>6. Test the fullscreen exam experience with security monitoring</li>
              </ol>
            </div>

            {/* Quick Links */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">🔗 Quick Links</h3>
              <div className="grid grid-cols-2 gap-4">
                <a 
                  href="/student-register" 
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  → Student Registration
                </a>
                <a 
                  href="/student-login" 
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  → Student Login
                </a>
                <a 
                  href="/quiz-manager" 
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  → Quiz Manager (Teachers)
                </a>
                <a 
                  href="/teacher-register" 
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  → Teacher Registration
                </a>
                <a 
                  href="/firebase-test" 
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  → Firebase Test
                </a>
                <a 
                  href="https://console.firebase.google.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  → Firebase Console
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}